import { VisitService } from '@/services/VisitService';
import {
  ClientSummaryByPriority,
  CmsVisitDataInputModelInput,
  HealthPromotion,
  Infographics,
  MoreInformation,
  Progress_VisitDataStatus,
  VisitVideos,
  DangerSignTranslation,
} from '@ecdlink/graphql';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { RootState, ThunkApiType } from '../types';

export interface VisitVideosWithLocale extends VisitVideos {
  locale: string;
}

export const VisitActions = {
  ADD_VISIT_FORM_DATA: 'addVisitFormData',
  ADD_VISIT_FOR_MOM_FORM_DATA: 'addVisitForMomFormData',
  GET_HEALTH_PROMOTION: 'getHealthPromotion',
  GET_VISIT_VIDEOS: 'getVisitVideos',
  GET_INFO_GRAPHICS: 'getInfographics',
  GET_DANGER_TRANSLATION: 'getDangerSignsTranslation',
  GET_MORE_INFORMATION: 'getMoreInformation',
  GET_PREVIOUS_VISIT_INFORMATION_FOR_INFANT:
    'getPreviousVisitInformationForInfant',
  GET_PREVIOUS_VISIT_INFORMATION_FOR_MOTHER:
    'getPreviousVisitInformationForMother',
  GET_MOTHER_SUMMARY_BY_PRIORITY: 'GetMotherSummaryByPriority',
  GET_INFANT_SUMMARY_BY_PRIORITY: 'GetInfantSummaryByPriority',
};

// in use
export const addVisitFormData = createAsyncThunk<
  any,
  CmsVisitDataInputModelInput,
  ThunkApiType<RootState>
>(
  VisitActions.ADD_VISIT_FORM_DATA,
  async (input, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
    } = getState();

    try {
      if (userAuth?.auth_token) {
        const response = await new VisitService(
          userAuth?.auth_token
        ).addVisitFormData(input);

        return response;
      }
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

// in use
export const getMoreInformation = createAsyncThunk<
  MoreInformation,
  { section: string; locale: string },
  ThunkApiType<RootState>
>(
  VisitActions.GET_MORE_INFORMATION,
  async ({ locale, section }, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
    } = getState();

    try {
      if (userAuth?.auth_token) {
        const [content] = await new VisitService(
          userAuth?.auth_token ?? ''
        ).getMoreInformation(section, locale);

        return content;
      } else {
        return rejectWithValue('no access token, profile check required');
      }
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

// in use
export const getHealthPromotion = createAsyncThunk<
  HealthPromotion,
  { section: string; locale: string; title?: string },
  ThunkApiType<RootState>
>(
  VisitActions.GET_HEALTH_PROMOTION,
  async ({ locale, section, title }, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
    } = getState();

    try {
      if (userAuth?.auth_token) {
        const [content] = await new VisitService(
          userAuth?.auth_token ?? ''
        ).getHealthPromotion(section, locale, title);

        return content;
      } else {
        return rejectWithValue('no access token, profile check required');
      }
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

// in use
export const getVisitVideos = createAsyncThunk<
  VisitVideosWithLocale[],
  { section: string; locale: string; title?: string },
  ThunkApiType<RootState>
>(
  VisitActions.GET_VISIT_VIDEOS,
  async ({ locale, section, title }, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
    } = getState();

    try {
      let content: VisitVideosWithLocale[] = [];

      if (userAuth?.auth_token) {
        const response = await new VisitService(
          userAuth?.auth_token ?? ''
        ).getVisitVideos(section, locale, title);

        content = response.map((item) => ({ ...item, locale }));
      } else {
        return rejectWithValue('no access token, profile check required');
      }

      if (!content) {
        return rejectWithValue('Error getting visit videos');
      }

      return content;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

// in use
export const getInfographics = createAsyncThunk<
  Infographics,
  { section: string; locale: string },
  ThunkApiType<RootState>
>(
  VisitActions.GET_INFO_GRAPHICS,
  async ({ locale, section }, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
    } = getState();

    try {
      if (userAuth?.auth_token) {
        const [content] = await new VisitService(
          userAuth?.auth_token ?? ''
        ).getInfographics(section, locale);

        return content;
      } else {
        return rejectWithValue('no access token, profile check required');
      }
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

// in use
export const getDangerSignsTranslation = createAsyncThunk<
  DangerSignTranslation[],
  { section: string; toTranslate: string },
  ThunkApiType<RootState>
>(
  VisitActions.GET_DANGER_TRANSLATION,
  async ({ toTranslate, section }, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
    } = getState();

    try {
      if (userAuth?.auth_token) {
        const content = await new VisitService(
          userAuth?.auth_token ?? ''
        ).GetDangerSignTranslations(section, toTranslate);

        return content;
      } else {
        return rejectWithValue('no access token, profile check required');
      }
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const getPreviousVisitInformationForInfant = createAsyncThunk<
  Progress_VisitDataStatus,
  { visitId: string },
  ThunkApiType<RootState>
>(
  VisitActions.GET_PREVIOUS_VISIT_INFORMATION_FOR_INFANT,
  async ({ visitId }, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
      visits: { previousVisitInformationForInfant },
    } = getState();

    try {
      let content: Progress_VisitDataStatus | undefined = undefined;

      if (userAuth?.auth_token) {
        content = await new VisitService(
          userAuth?.auth_token ?? ''
        ).getPreviousVisitInformationForInfant(visitId);
      } else {
        return rejectWithValue('no access token, profile check required');
      }

      if (!content) {
        return rejectWithValue(
          'Error getting previous visit information for infant'
        );
      }
      return content;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

// in use
export const getPreviousVisitInformationForMother = createAsyncThunk<
  Progress_VisitDataStatus,
  { visitId: string },
  ThunkApiType<RootState>
>(
  VisitActions.GET_PREVIOUS_VISIT_INFORMATION_FOR_MOTHER,
  async ({ visitId }, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
    } = getState();

    try {
      let content: Progress_VisitDataStatus | undefined = undefined;

      if (userAuth?.auth_token) {
        content = await new VisitService(
          userAuth?.auth_token ?? ''
        ).getPreviousVisitInformationForMother(visitId);
      } else {
        return rejectWithValue('no access token, profile check required');
      }

      if (!content) {
        return rejectWithValue(
          'Error getting previous visit information for infant'
        );
      }
      return content;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

// in use
export const getMotherSummaryByPriority = createAsyncThunk<
  ClientSummaryByPriority[],
  { visitId: string },
  ThunkApiType<RootState>
>(
  VisitActions.GET_MOTHER_SUMMARY_BY_PRIORITY,
  async ({ visitId }, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
    } = getState();
    try {
      let content: ClientSummaryByPriority[] | undefined = undefined;

      if (userAuth?.auth_token) {
        content = await new VisitService(
          userAuth?.auth_token ?? ''
        ).GetMotherSummaryByPriority(visitId);
      } else {
        return rejectWithValue('no access token, profile check required');
      }

      if (!content) {
        return rejectWithValue(
          'Error getting mother summary by priority for mother'
        );
      }
      return content;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

// in use
export const getInfantSummaryByPriority = createAsyncThunk<
  ClientSummaryByPriority[],
  { visitId: string },
  ThunkApiType<RootState>
>(
  VisitActions.GET_INFANT_SUMMARY_BY_PRIORITY,
  async ({ visitId }, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
    } = getState();
    try {
      let content: ClientSummaryByPriority[] | undefined = undefined;

      if (userAuth?.auth_token) {
        content = await new VisitService(
          userAuth?.auth_token ?? ''
        ).GetInfantSummaryByPriority(visitId);
      } else {
        return rejectWithValue('no access token, profile check required');
      }

      if (!content) {
        return rejectWithValue('Error getting infant summary by priority');
      }
      return content;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

// in use
export const addVisitForMomFormData = createAsyncThunk<
  any,
  CmsVisitDataInputModelInput,
  ThunkApiType<RootState>
>(
  VisitActions.ADD_VISIT_FOR_MOM_FORM_DATA,
  async (input, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
    } = getState();

    try {
      if (userAuth?.auth_token) {
        const response = await new VisitService(
          userAuth?.auth_token
        ).addVisitFormData(input);

        return response;
      }
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);
